import {
  WEBSOCKET_MIN_RECOVERY_INTERVAL_MS,
  WEBSOCKET_WATCHDOG_INTERVAL_MS,
} from './constants';

interface WebSocketWatchdogOptions {
  probe: () => Promise<void>;
  onDead: () => Promise<void>;
  intervalMs?: number;
  minRecoveryIntervalMs?: number;
}

export class WebSocketWatchdog {
  private readonly _probe: () => Promise<void>;
  private readonly _onDead: () => Promise<void>;
  private readonly _intervalMs: number;
  private readonly _minRecoveryIntervalMs: number;

  private _timer: ReturnType<typeof setInterval> | null = null;
  private _recoveryInFlight = false;
  private _lastRecoveryAt = 0;

  constructor(options: WebSocketWatchdogOptions) {
    this._probe = options.probe;
    this._onDead = options.onDead;
    this._intervalMs = options.intervalMs ?? WEBSOCKET_WATCHDOG_INTERVAL_MS;
    this._minRecoveryIntervalMs =
      options.minRecoveryIntervalMs ?? WEBSOCKET_MIN_RECOVERY_INTERVAL_MS;
  }

  start(): void {
    this.stop();
    this._timer = setInterval(() => this.tick(), this._intervalMs);
  }

  stop(): void {
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  private async tick(): Promise<void> {
    try {
      await this._probe();
    } catch {
      await this.triggerRecovery();
    }
  }

  private async triggerRecovery(): Promise<void> {
    if (this._recoveryInFlight) return;

    const now = Date.now();
    if (now - this._lastRecoveryAt < this._minRecoveryIntervalMs) return;

    this._recoveryInFlight = true;
    this._lastRecoveryAt = now;

    try {
      await this._onDead();
    } finally {
      this._recoveryInFlight = false;
    }
  }
}
